import React, { useState } from 'react';
import { FaMapMarkerAlt, FaEnvelope, FaPhone } from 'react-icons/fa';
import { useFetchWithCache } from '../hooks/useFetchWithCache';
import { api } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { PageBanner, BannerContent } from './styles/Shared.styles';
import {
  Section, Grid, InfoCol, InfoList, InfoItem, FormCard, Field, SubmitBtn, Note,
} from './styles/Contact.styles';

const Contact = () => {
  const { data: contact, loading } = useFetchWithCache('/contact');
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [note, setNote] = useState(null);

  if (loading && !contact) return <LoadingSpinner fullPage />;
  const c = contact || {};

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setNote(null);
    setSubmitting(true);
    try {
      await api.post('/contact/messages', form);
      setNote({ type: 'success', message: 'Thank you! Your message has been received.' });
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setNote({ type: 'error', message: err.message || 'Something went wrong.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageBanner $src={c.bannerImage}>
        <BannerContent>
          <span className="eyebrow">Contact</span>
          <h1>{c.title || 'Get in Touch'}</h1>
          {c.description && <p>{c.description}</p>}
        </BannerContent>
      </PageBanner>

      <Section>
        <Grid>
          <InfoCol>
            <span className="eyebrow">Reach Ehcerex</span>
            <h2>We'd love to hear from you</h2>
            <p>
              Whether you're a prospective partner, customer or just curious about our companies,
              reach out using the details below or send a message.
            </p>

            <InfoList>
              {c.address && (
                <InfoItem>
                  <div className="icon"><FaMapMarkerAlt /></div>
                  <div>
                    <div className="label">Address</div>
                    <div className="value">{c.address}</div>
                  </div>
                </InfoItem>
              )}
              {c.email && (
                <InfoItem>
                  <div className="icon"><FaEnvelope /></div>
                  <div>
                    <div className="label">Email</div>
                    <div className="value">{c.email}</div>
                  </div>
                </InfoItem>
              )}
              {Array.isArray(c.phones) && c.phones.length > 0 && (
                <InfoItem>
                  <div className="icon"><FaPhone /></div>
                  <div>
                    <div className="label">Phone</div>
                    <div className="value">
                      {c.phones.map((p, i) => <span key={i}>{p}</span>)}
                    </div>
                  </div>
                </InfoItem>
              )}
            </InfoList>
          </InfoCol>

          <FormCard onSubmit={onSubmit}>
            <h3>Send a message</h3>
            <p className="sub">We typically respond within 1&ndash;2 business days.</p>

            <Field>
              <label>Your Name</label>
              <input name="name" value={form.name} onChange={onChange} required />
            </Field>
            <Field>
              <label>Email</label>
              <input name="email" type="email" value={form.email} onChange={onChange} required />
            </Field>
            <Field>
              <label>Subject</label>
              <input name="subject" value={form.subject} onChange={onChange} />
            </Field>
            <Field>
              <label>Message</label>
              <textarea name="message" value={form.message} onChange={onChange} required />
            </Field>

            <SubmitBtn type="submit" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Message'}
            </SubmitBtn>

            {note && (
              <Note $error={note.type === 'error'}>{note.message}</Note>
            )}
          </FormCard>
        </Grid>
      </Section>
    </>
  );
};

export default Contact;
